<template>
    <section :class="$style.page">
        <div :class="$style.header">
            <div :class="$style.titleGroup">
                <Typography
                    is="h1"
                    type="header"
                    size="m"
                    :responsive="false"
                >
                    Native App Debug
                </Typography>
                <Typography
                    type="paragraph"
                    size="s"
                    :responsive="false"
                    :class="$style.muted"
                >
                    {{ bridgeStatus }}
                </Typography>
            </div>
            <GhostButton
                label="Profile"
                size="m"
                interfaceTarget="native-app-debug-profile"
                @click="emit('close')"
            />
        </div>

        <div :class="$style.workspace">
            <form
                :class="$style.editor"
                @submit.prevent="sendMessage"
            >
                <label :class="$style.field">
                    <Typography
                        type="label"
                        size="s"
                        :responsive="false"
                        :class="$style.label"
                    >
                        Action
                    </Typography>
                    <select
                        v-model="selectedActionKey"
                        :class="$style.select"
                        data-interface-target="native-app-debug-action"
                        @change="resetBody"
                    >
                        <optgroup
                            v-for="group in actionGroups"
                            :key="group.direction"
                            :label="group.label"
                        >
                            <option
                                v-for="action in group.actions"
                                :key="action.key"
                                :value="action.key"
                            >
                                {{ action.type }}
                            </option>
                        </optgroup>
                    </select>
                </label>

                <label :class="$style.field">
                    <Typography
                        type="label"
                        size="s"
                        :responsive="false"
                        :class="$style.label"
                    >
                        JSON body
                    </Typography>
                    <textarea
                        v-model="bodyText"
                        :class="[$style.textarea, bodyError && $style.invalid]"
                        spellcheck="false"
                        data-interface-target="native-app-debug-body"
                    />
                </label>

                <Typography
                    v-if="bodyError"
                    type="label"
                    size="s"
                    :responsive="false"
                    :class="$style.error"
                >
                    {{ bodyError }}
                </Typography>

                <div :class="$style.actions">
                    <Button
                        label="Send"
                        type="submit"
                        color="golden"
                        interfaceTarget="native-app-debug-send"
                        :disabled="Boolean(bodyError)"
                    />
                    <GhostButton
                        label="Reset"
                        interfaceTarget="native-app-debug-reset"
                        @click="resetBody"
                    />
                    <GhostButton
                        label="Clear log"
                        interfaceTarget="native-app-debug-clear-log"
                        @click="logEntries = []"
                    />
                </div>
            </form>

            <section :class="$style.log">
                <div :class="$style.logHeader">
                    <Typography
                        is="h2"
                        type="header"
                        size="xs"
                        :responsive="false"
                    >
                        Log
                    </Typography>
                    <Typography
                        type="label"
                        size="s"
                        :responsive="false"
                        :class="$style.muted"
                    >
                        {{ logEntries.length }}
                    </Typography>
                </div>

                <div
                    v-if="logEntries.length === 0"
                    :class="$style.empty"
                >
                    <Typography
                        type="paragraph"
                        size="s"
                        :responsive="false"
                        :class="$style.muted"
                    >
                        No messages yet.
                    </Typography>
                </div>

                <ol
                    v-else
                    :class="$style.logList"
                >
                    <li
                        v-for="entry in logEntries"
                        :key="entry.id"
                        :class="$style.logEntry"
                    >
                        <div :class="$style.logEntryMeta">
                            <Typography
                                type="label"
                                size="s"
                                :responsive="false"
                            >
                                {{ entry.title }}
                            </Typography>
                            <Typography
                                type="label"
                                size="s"
                                :responsive="false"
                                :class="$style.muted"
                            >
                                {{ entry.time }}
                            </Typography>
                        </div>
                        <pre :class="$style.logBody">{{ formatJson(entry.message) }}</pre>
                    </li>
                </ol>
            </section>
        </div>
    </section>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import Button from '@/components/Buttons/Button.vue'
import GhostButton from '@/components/Buttons/GhostButton.vue'
import Typography from '@/components/Typography.vue'
import { useNativeAppStore } from '@/store/native-app-store'
import { ADJUST_TOKENS } from '@/types/logger.types'

type NativeDebugDirection = 'portal-to-native' | 'native-to-portal'

type NativeDebugAction = {
    key: string
    type: string
    direction: NativeDebugDirection
    payload: Record<string, unknown>
}

type LogEntry = {
    id: number
    title: string
    time: string
    message: unknown
}

const GAMPACK_SKU = 'com.playgama.appstore.gampack.50'

const nativeAppStore = useNativeAppStore()
const { isAvailable } = storeToRefs(nativeAppStore)

const emit = defineEmits<{
    close: []
}>()

const actions: NativeDebugAction[] = [
    {
        key: 'purchase.getCapabilities',
        type: 'purchase.getCapabilities',
        direction: 'portal-to-native',
        payload: {},
    },
    {
        key: 'purchase.getProducts',
        type: 'purchase.getProducts',
        direction: 'portal-to-native',
        payload: {
            skus: [GAMPACK_SKU],
        },
    },
    {
        key: 'purchase.request',
        type: 'purchase.request',
        direction: 'portal-to-native',
        payload: {
            sku: GAMPACK_SKU,
            context: {
                source: 'shop',
                gameId: null,
            },
        },
    },
    {
        key: 'purchase.confirm',
        type: 'purchase.confirm',
        direction: 'portal-to-native',
        payload: {
            transactionId: 'debug-transaction-id',
            status: 'success',
        },
    },
    {
        key: 'purchase.trackBillingRevenue',
        type: 'purchase.trackBillingRevenue',
        direction: 'portal-to-native',
        payload: {
            eventToken: ADJUST_TOKENS.purchase_successful,
            revenue: 0.25,
            currency: 'EUR',
        },
    },
    {
        key: 'googleAuth.event',
        type: 'googleAuth.event',
        direction: 'portal-to-native',
        payload: {
            nonce: 'debug-nonce',
            clid: '',
            iosClid: '',
        },
    },
    {
        key: 'ads.init',
        type: 'ads.init',
        direction: 'portal-to-native',
        payload: {
            ppid: 'debug-ppid',
            targeting: {},
            timeout: 5000,
        },
    },
    {
        key: 'ads.requestFullscreen',
        type: 'ads.requestFullscreen',
        direction: 'portal-to-native',
        payload: {
            adPlacement: 'rewarded',
            adUnit: 'debug-ad-unit',
            slotId: 'debug-slot-id',
            targeting: {
                ad_placement: 'rewarded',
            },
        },
    },
    {
        key: 'ads.updateTargeting',
        type: 'ads.updateTargeting',
        direction: 'portal-to-native',
        payload: {
            targeting: {},
        },
    },
    {
        key: 'analytics.event',
        type: 'analytics.event',
        direction: 'portal-to-native',
        payload: {
            name: 'debug_event',
            event: 'custom_event',
            eventName: 'debug_event',
        },
    },
    {
        key: 'adjust.event',
        type: 'adjust.event',
        direction: 'portal-to-native',
        payload: {
            eventToken: 'debug-token',
        },
    },
    {
        key: 'changeOrientation',
        type: 'changeOrientation',
        direction: 'portal-to-native',
        payload: {
            action: 'lockLandscape',
        },
    },
    {
        key: 'native:auth.event',
        type: 'native:auth.event',
        direction: 'native-to-portal',
        payload: {
            credential: 'debug-credential',
        },
    },
    {
        key: 'native:ads.event',
        type: 'native:ads.event',
        direction: 'native-to-portal',
        payload: {
            slotId: 'debug-slot-id',
            event: 'rewardedSlotGranted',
        },
    },
    {
        key: 'native:purchase.capabilities',
        type: 'native:purchase.capabilities',
        direction: 'native-to-portal',
        payload: {
            available: true,
            canMakePayments: true,
        },
    },
    {
        key: 'native:purchase.products',
        type: 'native:purchase.products',
        direction: 'native-to-portal',
        payload: {
            products: [
                {
                    sku: GAMPACK_SKU,
                    localizedTitle: '50 Gam',
                    localizedPrice: '$4.99',
                    available: true,
                },
            ],
        },
    },
    {
        key: 'native:purchase.result',
        type: 'native:purchase.result',
        direction: 'native-to-portal',
        payload: {
            status: 'purchased',
            sku: GAMPACK_SKU,
            transactionId: 'debug-transaction-id',
            receipt: 'debug-receipt-or-token',
        },
    },
    {
        key: 'native:purchase.unfinished',
        type: 'native:purchase.unfinished',
        direction: 'native-to-portal',
        payload: {
            transactions: [
                {
                    sku: GAMPACK_SKU,
                    transactionId: 'debug-transaction-id',
                    receipt: 'debug-receipt-or-token',
                },
            ],
        },
    },
]

const actionGroups = [
    {
        direction: 'portal-to-native',
        label: 'Portal to native',
        actions: actions.filter((action) => action.direction === 'portal-to-native'),
    },
    {
        direction: 'native-to-portal',
        label: 'Native to portal',
        actions: actions.filter((action) => action.direction === 'native-to-portal'),
    },
] as const

const defaultAction = actions[0] as NativeDebugAction
const selectedActionKey = ref(defaultAction.key)
const bodyText = ref(formatJson(defaultAction.payload))
const logEntries = ref<LogEntry[]>([])

const selectedAction = computed<NativeDebugAction>(
    () => actions.find((action) => action.key === selectedActionKey.value) ?? defaultAction,
)
const bridgeStatus = computed(() => (isAvailable.value ? 'Native bridge available' : 'Native bridge unavailable'))
const bodyError = computed(() => {
    try {
        const value = JSON.parse(bodyText.value)

        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            return 'Body must be a JSON object.'
        }

        return ''
    } catch (error) {
        return error instanceof Error ? error.message : 'Invalid JSON.'
    }
})

let logId = 0
let unsubscribeIncoming: (() => void) | undefined
let unsubscribeOutgoing: (() => void) | undefined

function formatJson(value: unknown) {
    return JSON.stringify(value, null, 4)
}

function getTime() {
    return new Date().toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })
}

function addLog(title: string, message: unknown) {
    logEntries.value.unshift({
        id: ++logId,
        title,
        time: getTime(),
        message,
    })
}

function getParsedBody() {
    return JSON.parse(bodyText.value) as Record<string, unknown>
}

function resetBody() {
    bodyText.value = formatJson(selectedAction.value.payload)
}

function sendMessage() {
    if (bodyError.value) {
        return
    }

    const payload = getParsedBody()

    if (selectedAction.value.direction === 'portal-to-native') {
        nativeAppStore.postMessage(selectedAction.value.type, payload)
        return
    }

    const message = {
        type: selectedAction.value.type,
        payload,
    }
    addLog('Debug tool sent native message', message)
    nativeAppStore.dispatchDebugMessage(message)
}

watch(selectedActionKey, resetBody)

onMounted(() => {
    unsubscribeIncoming = nativeAppStore.addMessageHandler((data) => {
        addLog('Native message received', data)
    })
    unsubscribeOutgoing = nativeAppStore.addOutgoingMessageHandler((message) => {
        addLog('Portal sent native action', message)
    })
})

onUnmounted(() => {
    unsubscribeIncoming?.()
    unsubscribeOutgoing?.()
})
</script>

<style module>
.page {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding-top: 4px;
}

.header {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.titleGroup {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.muted {
    color: var(--white-60);
}

.workspace {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
}

.editor,
.log {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
    padding: 16px;
    border: 1px solid var(--white-08);
    border-radius: 8px;
    background: var(--carbon-500);
}

.field {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.label {
    color: var(--white-60);
}

.select,
.textarea {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid var(--white-08);
    border-radius: 8px;
    color: var(--white);
    background: var(--carbon-400);
    font-family: var(--font-family);
    font-size: 15px;
    line-height: 20px;
}

.select {
    min-height: 44px;
    padding: 0 12px;
}

.textarea {
    min-height: 360px;
    padding: 12px;
    resize: vertical;
    font-family: monospace;
}

.invalid {
    border-color: var(--rose-400);
}

.error {
    color: var(--rose-400);
}

.actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.logHeader,
.logEntryMeta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.empty {
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed var(--white-08);
    border-radius: 8px;
}

.logList {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 620px;
    overflow: auto;
}

.logEntry {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    background: var(--carbon-400);
}

.logBody {
    margin: 0;
    overflow: auto;
    color: var(--white);
    font-size: 13px;
    line-height: 18px;
    white-space: pre-wrap;
    word-break: break-word;
}

@media (--tablet-plus) {
    .header {
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;
    }
}

@media (--desktop-small-plus) {
    .workspace {
        grid-template-columns: minmax(420px, 0.95fr) minmax(360px, 1.05fr);
        align-items: start;
    }
}
</style>
